import React from "react";
import DynamicText, { DynamiceContentProps } from "@directv-digital/dds.ui.dynamic-text";
import { colorPalette } from "@directv-digital/dds.ui.dtv-theme";
import FusionTheme from "@directv-digital/fusion.themes.fusion";

interface CardLegalTextProps {
  legalText: DynamiceContentProps;
  cardId?: string;
  side: "front" | "back";
  fallbackColor?: string;
}

const CardLegalText = ({ legalText, cardId, side, fallbackColor = "text.secondary" }: CardLegalTextProps) => (
  // DynamicText latches `content` into internal useState on mount and only refreshes that
  // state on resize when `dynamicContent` is present (see dds.ui.dynamic-text). With a plain
  // `content` prop it ignores later prop changes — so the client-side RSN update
  // (e.g. $74.99 → $92.98) never reaches the DOM. Key on content to force a remount when the
  // resolved legal copy changes; the stable `id` is kept for a11y/analytics.
  <DynamicText
    key={legalText.content}
    component={legalText.component || "div"}
    content={legalText.content}
    id={cardId ? `${cardId}-${side}-legalText` : undefined}
    variant={legalText.variant || "Labelsmall"}
    cta={legalText.cta ? {
      ...legalText.cta,
      sx: {
        color: legalText.cta?.color || "text.secondary",
        fontFamily: "inherit",
        fontSize: "inherit",
        fontWeight: "inherit"
      }
    } : undefined}
    sx={{
      pt: "12px",
      lineHeight: 1.3,
      fontWeight: 400,
      fontFamily: "roboto-regular, sans-serif",
      "& strong, & b": {
        fontFamily: "roboto-medium, sans-serif",
        fontWeight: "500 !important"
      },
      color: legalText.color || fallbackColor,
      // CMS can pass fontSize to adjust per-card; defaults to AC-3 spec of 12px.
      fontSize: legalText.fontSize || "12px",
      // Hover: underline avoids layout-shift (bold changes text width).
      // Focus-visible: explicit outline matches FOCUS_RING_SX used in FrontCard/BackCard.
      "& a:hover": { textDecoration: "underline" },
      "& a:focus-visible": {
        outline: `2px solid ${colorPalette.blue[600]}`,
        outlineOffset: "2px",
        borderRadius: FusionTheme.fusion.shapes["shape-extra-small"]
      }
    }}
  />
);

export default React.memo(CardLegalText);
